import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { GetglobalsettingsService } from './getglobalsettings.service';
import { AgentBankCandidateInvoice, AgentBankPaymentReviewItem } from '../shared/agent-bank-payment-review';

/**
 * REST client for the bank-payment review MVP,
 * `/api/agent/bank-payment-review` on the webapi backend. See
 * `docs/PROMPT_agent-bank-payment-review-mvp.md` for the full contract.
 *
 * Every route is `realm:finance-manager` OR `realm:portal-admin` server-side;
 * this service does not itself gate access — the host component/page does.
 * Errors are NOT caught/alerted here — callers subscribe with their own
 * `error` handler and surface it via `MessageService` toast.
 *
 * Deliberately no `retry(1)` on `ignore()`/`reopen()`/`process()` — unlike
 * most services in this app, these calls change review state on a shared
 * queue a second manager may also be acting on; the spec's own "never
 * silently create a second payment on an uncertain response" caution
 * applies just as much to blindly retrying a write whose actual outcome on
 * the server is unknown. The backend already tolerates an identical repeat
 * of `/process` as a harmless no-op, but this service doesn't lean on that
 * to justify an automatic retry here.
 */
@Injectable({
  providedIn: 'root'
})
export class AgentBankPaymentReviewApiService {

  constructor(
    private http: HttpClient,
    private globalsettingsservice: GetglobalsettingsService,
  ) {
  }

  /** Builds an absolute API URL by prepending the runtime API server address. */
  private _getApiUrl(url: string = ''): string {
    return this.globalsettingsservice.getApiServerUrl() + url;
  }

  /** Fresh standard headers per call so Setup-screen overrides take effect
   *  without a page reload. */
  private _options() {
    return { headers: this.globalsettingsservice.getStandardApiHeaders() };
  }

  /** `GET /api/agent/bank-payment-review?review_status=...` — list rows by
   *  review state. `pending` (the default) is the actual work queue,
   *  additionally scoped server-side to eligible incoming transactions;
   *  `processed`/`ignored` are administrative views. */
  getList(reviewStatus: 'pending' | 'processed' | 'ignored' = 'pending'): Observable<AgentBankPaymentReviewItem[]> {
    return this.http.get<AgentBankPaymentReviewItem[]>(
      this._getApiUrl('/api/agent/bank-payment-review?review_status=' + reviewStatus),
      this._options()
    );
  }

  /** `PATCH /:id/ignore` — pending -> ignored. Idempotent if already ignored. */
  ignoreTransaction(id: number): Observable<{ id: number; review_status: string }> {
    return this.http.patch<{ id: number; review_status: string }>(
      this._getApiUrl('/api/agent/bank-payment-review/' + id + '/ignore'),
      {},
      this._options()
    );
  }

  /** `PATCH /:id/reopen` — ignored -> pending only (undoes an accidental Ignore). */
  reopenTransaction(id: number): Observable<{ id: number; review_status: string }> {
    return this.http.patch<{ id: number; review_status: string }>(
      this._getApiUrl('/api/agent/bank-payment-review/' + id + '/reopen'),
      {},
      this._options()
    );
  }

  /** `PATCH /:id/process` — records a payment already created client-side.
   *  Both DOCIDs are verified server-side before anything is written; call
   *  this only after `CreateNewDocumentComponent`'s own save has already
   *  succeeded and returned a real `documentId`. */
  processTransaction(
    id: number,
    paymentDocumentId: number,
    invoiceDocumentId: number
  ): Observable<{ id: number; review_status: string; payment_document_id: number }> {
    return this.http.patch<{ id: number; review_status: string; payment_document_id: number }>(
      this._getApiUrl('/api/agent/bank-payment-review/' + id + '/process'),
      { payment_document_id: paymentDocumentId, invoice_document_id: invoiceDocumentId },
      this._options()
    );
  }

  /** `GET /:id/candidate-invoices` -- read-only. The open invoices this
   *  transaction's counterparty could be paying, for the split-across-invoices
   *  dialog. A 404 means an unknown transaction id; no EDRPOU or no matching
   *  client is a normal empty array. */
  getCandidateInvoices(id: number): Observable<AgentBankCandidateInvoice[]> {
    return this.http.get<AgentBankCandidateInvoice[]>(
      this._getApiUrl('/api/agent/bank-payment-review/' + id + '/candidate-invoices'),
      this._options()
    );
  }
}
